/* eslint-disable react/no-unused-prop-types */
import {
    ReactNode, useCallback, useMemo,
} from 'react';
import { useReduxForm } from '@rainbow-modules/hooks';
import { PicklistValue } from '@rainbow-modules/listview/lib/components/AuditLogs/types';
import { Option } from 'react-rainbow-components';
import { StyledPicklist } from 'components/Instructions/aiModelConfigForm/styled';
import useGptModels from 'data/hooks/useGptModels';
import { TriangleAlert } from 'lucide-react';
import { DateTime } from 'luxon';
import { ModelLabel, ShutdownWarning } from './styled';

const getShutdownWarning = (date: string) => {
    const shutdown = DateTime.fromISO(date).setLocale('en').startOf('day');
    const today = DateTime.now().startOf('day');
    if (!shutdown.isValid) return `Shuts down ${date}`;
    if (shutdown.hasSame(today, 'day')) return 'Shuts down today';
    const relative = shutdown.toRelative({ base: today, unit: ['years', 'months', 'days'] });
    return `${shutdown < today ? 'Shut down' : 'Shuts down'} ${relative}`;
};

interface GPT4ModelPickerProps {
    responders?: boolean;
    id?: string;
    className?: string;
    name?: string;
    label?: string;
    tabIndex?: number | string;
    placeholder?: string;
    error?: ReactNode;
    required?: boolean;
    value?: string;
    onChange?: (value: string) => void;
}

const GPT4ModelPicker = (props: GPT4ModelPickerProps) => {
    const { responders } = props;
    const {
        value: valueInProps,
        onChange = () => {},
        id,
        className,
        name,
        label,
        tabIndex,
        placeholder,
        error,
        required,
    } = useReduxForm(props);
    const {
        data, isFetching, isError, refreshIfStale,
    } = useGptModels(responders);
    const models = data?.data;

    const pickListvalue = useMemo(() => {
        if (!valueInProps) return undefined;
        const modelFound = models?.find((model) => model.id === valueInProps);
        return {
            name: valueInProps,
            label: modelFound?.name || valueInProps,
            value: valueInProps,
        };
    }, [models, valueInProps]);

    const handleOnChange = useCallback(
        (value: PicklistValue) => onChange(value.value),
        [onChange],
    );

    return (
        <div onKeyDownCapture={refreshIfStale}>
            <StyledPicklist
                id={id}
                name={name}
                className={className}
                label={label}
                labelAlignment="left"
                tabIndex={tabIndex}
                required={required}
                placeholder={placeholder}
                value={pickListvalue}
                onChange={handleOnChange}
                onFocus={refreshIfStale}
                onClick={refreshIfStale}
                isLoading={isFetching}
                enableSearch
                error={isError ? 'Unable to load GPT models. Open the dropdown to try again.' : error}
            >
                {models?.length === 0 && (
                    <Option name="no-models" label="No GPT models are available." disabled />
                )}
                {models?.map((model) => (
                    <Option
                        key={`gpt4-picker-option__${model.id}`}
                        name={model.id}
                        label={(
                            <ModelLabel>
                                <span>{model.name}</span>
                                {model.shutdown_date?.trim() && (
                                    <ShutdownWarning title={`Shutdown date: ${model.shutdown_date}`}>
                                        <TriangleAlert size={14} aria-hidden="true" />
                                        {getShutdownWarning(model.shutdown_date)}
                                    </ShutdownWarning>
                                )}
                            </ModelLabel>
                        )}
                        searchableText={model.name}
                        value={model.id}
                        disabled={model.capabilities?.status === 'unsupported'}
                    />
                ))}
            </StyledPicklist>
        </div>
    );
};

export default GPT4ModelPicker;
